import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { FaArrowLeftLong, FaArrowRightLong } from "react-icons/fa6";
import { easing } from "../utils/motionVariants";
import "./FeaturedSlider.css";

export default function FeaturedSlider({ projects = [], title = "Featured Work" }) {
  const navigate = useNavigate();
  const viewportRef = useRef(null);
  const slideRef = useRef(null);
  const [slideW, setSlideW] = useState(0);
  const [index, setIndex] = useState(0);
  const [perView, setPerView] = useState(2);

  useEffect(() => {
    const measure = () => {
      const vp = viewportRef.current;
      const first = slideRef.current;
      if (!vp || !first) return;
      const vw = vp.clientWidth;
      const isMobile = vw < 900;
      setPerView(isMobile ? 1 : 2);
      setSlideW(first.clientWidth + 22);
    };
    measure();
    const ro = new ResizeObserver(measure);
    if (viewportRef.current) {
      ro.observe(viewportRef.current);
    }
    return () => ro.disconnect();
  }, []);

  const maxIndex = useMemo(() => Math.max(0, projects.length - perView), [projects.length, perView]);
  const prev = () => setIndex((i) => Math.max(0, i - 1));
  const next = () => setIndex((i) => Math.min(maxIndex, i + 1));

  useEffect(() => {
    if (index > maxIndex) setIndex(maxIndex);
  }, [maxIndex, index]);

  return (
    <div className="featured">
      <div className="section-header" style={{ marginBottom: 8 }}>
        <h2>{title}</h2>
        <div className="section-underline" />
      </div>
      <p className="muted" style={{ marginTop: 0 }}>
        A glimpse into my professional journey.
      </p>

      <div className="slider">
        <motion.button
          className="nav prev"
          onClick={prev}
          disabled={index === 0}
          aria-label="Previous"
          whileHover={index !== 0 ? { scale: 1.1, x: -3 } : {}}
          whileTap={index !== 0 ? { scale: 0.9 } : {}}
          transition={{ duration: 0.2 }}
        >
          <FaArrowLeftLong />
        </motion.button>
        <div className="viewport" ref={viewportRef}>
          <motion.div
            className="track"
            animate={{ x: -index * slideW }}
            transition={{ duration: 0.55, ease: easing }}
          >
            {projects.map((p, i) => (
              <motion.article
                key={p.slug}
                className="slide-card"
                ref={i === 0 ? slideRef : undefined}
                onClick={() => navigate(`/project/${p.slug}`)}
                whileHover={{ y: -8, scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                transition={{ duration: 0.25, ease: easing }}
              >
                {p.cover && <img src={p.cover} alt={p.title} />}
                <div className="slide-body">
                  <div className="slide-url muted">{p.links?.live?.replace(/^https?:\/\//, "") || p.clientOrOrg}</div>
                  <h3 className="slide-title">{p.title}</h3>
                  <p className="muted">{p.oneLiner}</p>
                </div>
              </motion.article>
            ))}
          </motion.div>
        </div>
        <motion.button
          className="nav next"
          onClick={next}
          disabled={index === maxIndex}
          aria-label="Next"
          whileHover={index !== maxIndex ? { scale: 1.1, x: 3 } : {}}
          whileTap={index !== maxIndex ? { scale: 0.9 } : {}}
          transition={{ duration: 0.2 }}
        >
          <FaArrowRightLong />
        </motion.button>
      </div>
    </div>
  );
}
